import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const fsdpComponent: ComponentDef = {
  id: 'fsdp',
  name: 'FSDP',
  description: 'Fully Sharded Data Parallel shards parameters, gradients, and optimizer states across GPUs.',
  category: 'parallelism',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_gpus', label: 'GPUs', type: 'number', default: 4, min: 2, max: 1024, step: 1 },
    {
      name: 'variant',
      label: 'View',
      type: 'select',
      default: 'overview',
      options: [
        { label: 'Overview', value: 'overview' },
        { label: 'Sharding Stages', value: 'sharding-stages' },
        { label: 'Communication', value: 'communication' },
        { label: 'ZeRO Stage 1', value: 'zero1' },
        { label: 'ZeRO Stage 2', value: 'zero2' },
        { label: 'ZeRO Stage 3', value: 'zero3' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_gpus = Number(params.n_gpus);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [d_model]);

    if (variant === 'overview') {
      // High-level FSDP overview: shard -> gather -> compute -> reduce-scatter -> update
      g.split('shard', 'x', [d_model / n_gpus], `/${n_gpus}`);
      g.linear('local', 'shard', [d_model / n_gpus, d_model / n_gpus]);
      g.allgather('params', 'local', [d_model]);
      g.reduceScatter('grads', 'params', [d_model / n_gpus]);
      g.scale('update', 'grads', 'Optimizer');
      g.output('out', 'update');
    } else if (variant === 'sharding-stages') {
      // Show FSDP sharding stages: what is stored locally vs gathered
      // Stage 1: Local sharded parameters
      g.split('param_shard', 'x', [d_model / n_gpus], `params/${n_gpus}`);
      g.linear('sharded_forward', 'param_shard', [d_model / n_gpus, d_model / n_gpus]);

      // Stage 2: AllGather for full params (temporarily)
      g.allgather('full_params', 'sharded_forward', [d_model]);

      // Stage 3: Forward with full params, then discard
      g.linear('full_forward', 'full_params', [d_model, d_model]);
      g.metric('param_mem', 'full_forward', `Temp: full ${d_model}×${d_model}`);

      // Stage 4: ReduceScatter gradients back to shards
      g.reduceScatter('grad_shard', 'full_forward', [d_model / n_gpus]);

      // Stage 5: Sharded optimizer state
      g.split('opt_shard', 'grad_shard', [d_model / n_gpus], `opt/${n_gpus}`);
      g.scale('local_update', 'opt_shard', `Optimizer 1/${n_gpus}`);
      g.output('out', 'local_update');
    } else if (variant === 'communication') {
      // Communication view: show the AllGather and ReduceScatter pattern
      g.split('shard', 'x', [d_model / n_gpus], `/${n_gpus}`);

      // Pre-forward: AllGather params
      g.allgather('ag_params', 'shard', [d_model]);
      g.metric('ag_cost', 'ag_params', `AllGather: ${d_model} elems`);

      // Forward computation
      g.linear('forward', 'ag_params', [d_model, d_model]);

      // Post-backward: ReduceScatter gradients
      g.reduceScatter('rs_grads', 'forward', [d_model / n_gpus]);
      g.metric('rs_cost', 'rs_grads', `ReduceScatter: ${d_model} elems`);

      // Optimizer step (local)
      g.scale('update', 'rs_grads', 'Optimizer (local)');

      // Show communication overlap opportunity
      g.p2p('next_layer_ag', 'update', [d_model / n_gpus]);
      g.metric('overlap', 'next_layer_ag', 'Overlap AG with compute');
      g.output('out', 'overlap');
    } else if (variant === 'zero1') {
      // ZeRO Stage 1: Shard optimizer states only
      // Forward/backward run normally with full params
      g.linear('forward', 'x', [d_model, d_model]);
      g.metric('fwd', 'forward', 'Full forward/backward');

      // Gradients are fully computed on each rank
      g.scale('grads', 'forward', 'Backward');

      // Optimizer step: only update local shard
      g.split('opt_shard', 'grads', [d_model / n_gpus], `opt/${n_gpus}`);
      g.scale('local_update', 'opt_shard', `Optimizer 1/${n_gpus}`);
      g.metric('opt_mem', 'local_update', `Optimizer states: 1/${n_gpus}`);

      // AllGather updated params so all ranks have full params
      g.allgather('full_params', 'local_update', [d_model]);
      g.metric('ag_cost', 'full_params', `AllGather: ${d_model} elems`);
      g.output('out', 'full_params');
    } else if (variant === 'zero2') {
      // ZeRO Stage 2: Shard gradients + optimizer states
      // Forward normally with full params
      g.linear('forward', 'x', [d_model, d_model]);
      g.metric('fwd', 'forward', 'Full forward/backward');

      // ReduceScatter gradients: each rank keeps only its shard
      g.reduceScatter('grad_shard', 'forward', [d_model / n_gpus]);
      g.metric('rs_cost', 'grad_shard', `ReduceScatter: ${d_model} elems`);

      // Optimizer step on local shard only
      g.scale('local_update', 'grad_shard', `Optimizer 1/${n_gpus}`);
      g.metric('opt_mem', 'local_update', `Grads + opt: 1/${n_gpus}`);

      // AllGather updated params
      g.allgather('full_params', 'local_update', [d_model]);
      g.metric('ag_cost', 'full_params', `AllGather: ${d_model} elems`);
      g.output('out', 'full_params');
    } else {
      // ZeRO Stage 3: Shard everything (params, gradients, optimizer states)
      // Params are sharded: each rank holds 1/n_gpus of params
      g.split('param_shard', 'x', [d_model / n_gpus], `params/${n_gpus}`);
      g.metric('param_mem', 'param_shard', `Params: 1/${n_gpus}`);

      // AllGather params before forward (temporarily full)
      g.allgather('full_params', 'param_shard', [d_model]);
      g.metric('ag_cost', 'full_params', `AllGather: ${d_model} elems`);

      // Forward with full params, then discard
      g.linear('forward', 'full_params', [d_model, d_model]);

      // ReduceScatter gradients: each rank keeps its shard
      g.reduceScatter('grad_shard', 'forward', [d_model / n_gpus]);
      g.metric('rs_cost', 'grad_shard', `ReduceScatter: ${d_model} elems`);

      // Optimizer on local shard only
      g.scale('local_update', 'grad_shard', `Optimizer 1/${n_gpus}`);
      g.metric('opt_mem', 'local_update', `All states: 1/${n_gpus}`);
      g.output('out', 'local_update');
    }

    return g.build();
  }
};
